import { useEffect, useState } from "react";
import { useProject } from "@/contexts/ProjectContext";
import api from "@/lib/api";
import { BDT, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Wallet, TrendingUp, Users, AlertCircle, FolderOpen, ListTodo } from "lucide-react";

function MetricCard({ label, value, icon: Icon, accent }) {
  return (
    <Card className="rounded-2xl border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className={`h-10 w-10 rounded-lg grid place-items-center ${accent}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-1">{label}</div>
        <div className="font-display text-2xl lg:text-3xl font-bold text-slate-900 tracking-tighter">{value}</div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { selected, myPermissions } = useProject();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selected) return;
    let active = true;
    setLoading(true);
    api.get(`/projects/${selected.id}/dashboard`).then((res) => {
      if (active) setData(res.data);
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [selected]);

  if (!selected) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center">
        <h2 className="font-display text-xl font-semibold text-slate-900 mb-2">No project selected</h2>
        <p className="text-sm text-slate-500">Use the project switcher in the top bar to choose a project.</p>
      </div>
    );
  }

  const canAccounts = myPermissions.includes("accounts");
  const canTasks = myPermissions.includes("tasks");
  const canResources = myPermissions.includes("resources");

  return (
    <div className="space-y-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{selected.name}</h1>
          <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
            <span>{selected.client_name}</span>
            <span>•</span>
            <Badge variant="secondary" className="text-xs">{selected.status}</Badge>
          </div>
          {selected.description && (
            <p className="text-sm text-slate-600 mt-3 max-w-2xl">{selected.description}</p>
          )}
        </div>
      </header>

      {loading && <div className="text-sm text-slate-500">Loading dashboard...</div>}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {canAccounts && (
              <>
                <MetricCard
                  label="Client Due"
                  value={BDT(data.client_due)}
                  icon={Wallet}
                  accent="bg-rose-50 text-rose-700"
                />
                <MetricCard
                  label="Client Paid"
                  value={BDT(data.client_paid)}
                  icon={TrendingUp}
                  accent="bg-emerald-50 text-emerald-700"
                />
                <MetricCard
                  label="Team Expense"
                  value={BDT(data.team_total)}
                  icon={Users}
                  accent="bg-indigo-50 text-indigo-700"
                />
              </>
            )}
            {canTasks && (
              <MetricCard
                label="Open Tasks"
                value={data.open_tasks ?? 0}
                icon={ListTodo}
                accent="bg-amber-50 text-amber-700"
              />
            )}
          </div>

          {canTasks && (
            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-rose-500" />
                    <h2 className="font-display text-lg font-semibold text-slate-900">Urgent Tasks</h2>
                  </div>
                  <Badge className="bg-rose-50 text-rose-700 border border-rose-200">{data.urgent_tasks ?? 0}</Badge>
                </div>
                <div className="space-y-2">
                  {(data.recent_tasks || []).filter(t => t.priority === "Urgent").map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`urgent-task-${t.id}`}>
                      <div>
                        <div className="text-sm font-medium text-slate-900">{t.title}</div>
                        {t.due_date && <div className="text-xs text-slate-500">Due: {formatDate(t.due_date)}</div>}
                      </div>
                      <Badge className="bg-rose-50 text-rose-700 border border-rose-200 text-[10px]">Urgent</Badge>
                    </div>
                  ))}
                  {(data.recent_tasks || []).filter(t => t.priority === "Urgent").length === 0 && (
                    <div className="text-sm text-slate-500">No urgent tasks. </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {canTasks && (
              <Card className="rounded-2xl border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <h2 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <ListTodo className="h-4 w-4 text-indigo-600" /> Recent Tasks
                  </h2>
                  <div className="space-y-2">
                    {(data.recent_tasks || []).slice(0, 5).map((t) => (
                      <div key={t.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50">
                        <div className="text-sm text-slate-700">{t.title}</div>
                        <Badge variant="secondary" className="text-[10px]">{t.priority}</Badge>
                      </div>
                    ))}
                    {(!data.recent_tasks || data.recent_tasks.length === 0) && (
                      <div className="text-sm text-slate-500">No tasks yet.</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {canResources && (
              <Card className="rounded-2xl border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <h2 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-indigo-600" /> Recent Resources
                  </h2>
                  <div className="space-y-2">
                    {(data.recent_resources || []).slice(0, 5).map((r) => (
                      <div key={r.id} className="p-3 rounded-lg hover:bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-slate-800">{r.title}</div>
                          <Badge variant="secondary" className="text-[10px]">{r.type}</Badge>
                        </div>
                        {r.description && <div className="text-xs text-slate-500 mt-1 truncate">{r.description}</div>}
                      </div>
                    ))}
                    {(!data.recent_resources || data.recent_resources.length === 0) && (
                      <div className="text-sm text-slate-500">No resources yet.</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
